import React, { FC, useState, useEffect } from 'react'
import { PlayIcon } from '@heroicons/react/solid'
import { Spinner } from '../Spinner'
import { StyledVideoPlayer } from '.'

type Props = {
  videoId?: string;
  resumeProgress?: boolean;
  hasTitle?: boolean;
  hasLinks?: boolean;
  hasLogo?: boolean;
  onStart?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
}

const VideoPlayer: FC<Props> = ({
  videoId,
  resumeProgress = true,
  hasTitle,
  hasLinks,
  hasLogo,
  onStart,
  onPlay,
  onPause,
  onEnd
}) => {
  const [videoPlayer, setVideoPlayer] = useState<any>()
  const [showSplash, setShowSplash] = useState(true)
  const [loading, setLoading] = useState(false)

  const startPlayer = () => {
    if (!videoPlayer) {
      return
    }

    videoPlayer.play()
    setShowSplash(false)

    if (onStart) {
      onStart()
    }
  }

  // Loads the video from Muse.AI and initalizes the player
  const initPlayer = () => {
    if (videoPlayer) {
      return videoPlayer.setVideo(videoId)
    }

    // References the MusePlayer from the window, since they don't have a package
    const { MusePlayer } = window as any
    const player = MusePlayer({
      container: '#player',
      title: hasTitle,
      links: hasLinks,
      logo: hasLogo,
      resume: resumeProgress,
      width: '100%',
      sizing: 'fill',
      css: '.player-cover-play, .player-cover-search { display: none !important; }'
    })

    setLoading(true)
    setVideoPlayer(player)

    player.on('metadata', () => setLoading(false))
    player.on('play', () => handleEvent('play'))
    player.on('pause', () => handleEvent('pause'))
    player.on('ended', () => handleEvent('ended'))

    player.setVideo(videoId)
  }

  const handleEvent = (event: 'play' | 'pause' | 'ended') => {
    if (event === 'play' && onPlay) {
      return onPlay()
    }

    if (event === 'pause' && onPause) {
      return onPause()
    }

    if (event === 'ended' && onEnd) {
      return onEnd()
    }
  }

  useEffect(() => {
    if (videoId) {
      initPlayer()
    }
  }, [videoId])

  return (
    <StyledVideoPlayer className="video-player" loading={loading}>
      {loading && <Spinner />}
      {showSplash && (
        <div onClick={() => startPlayer()} className="fake-splash">
          <PlayIcon />
        </div>
      )}
      <div id="player" className="player" />
    </StyledVideoPlayer>
  )
}

export default VideoPlayer
